import { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { slugify } from "tsi-common-react/src/utils/text";

import type { ToolboxContent, TabsBlock as Value } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type Tab = NonNullable<Value["tabs"]>[number];

type Props = Immutable<{
    value: Value;
}> & {
    /**
     * Renders a tab panel's ``ToolboxContent`` stream. Supplied by the caller
     * (the recursive ``tabs_block`` map entry passes a callback that closes over
     * the toolbox map). This render-prop keeps ``TabsBlock`` free of any
     * import of ``toolboxComponentsMap`` -- no import cycle, and no
     * ``StreamFieldBlockMap`` on the prop surface (the map's variance is checked
     * at the caller's ``<StreamField>`` call, sealy-style).
     */
    renderPanel: (panel: Immutable<ToolboxContent>) => React.ReactNode;
};

/**
 * Faithful port of the legacy server-rendered tempurpedic
 * ``cms/blocks/tabs-block.html`` (``ToolboxContainers`` key ``tabs_block``;
 * #33460). IDs reproduce the legacy ``tab-{name|slugify}-{counter}`` /
 * ``panel-{name|slugify}-{counter}`` (1-based) so each button's
 * ``aria-controls`` equals its panel's ``id``, and each panel renders its
 * ``ToolboxContent`` via ``renderPanel`` (the recursive toolbox dispatch).
 *
 * Tab switching is owned by this component (``useState`` active index +
 * ``onClick``/``onKeyDown``), like the sibling ``FAQsBlock`` / ``TabCardsBlock``.
 * Utility pages render as a full-document ``hydrateRoot(document)`` tree, where a
 * hydration mismatch re-creates the tab subtree; the former external switcher
 * (``ui/tabs.ts`` ``activateTabs('.tabs-block')`` bound once at
 * ``DOMContentLoaded``) was then left bound to the discarded SSR nodes, so only
 * the first (default-visible) panel worked (#34498). React state binds to
 * whatever nodes React commits, so it survives the re-create. Active index 0
 * renders markup byte-identical to the legacy template, so no new mismatch is
 * introduced.
 */
const slugifyName = (name: string | null): string => slugify(name ?? "");

export const TabsBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const tabs: Immutable<Tab[]> = v.tabs ?? [];
    const [activeIndex, setActiveIndex] = React.useState(0);
    const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

    const selectTab = (index: number) => {
        setActiveIndex(index);
        tabRefs.current[index]?.focus();
    };

    const onKeyDown = (event: React.KeyboardEvent, index: number) => {
        if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
            return;
        }
        const delta = event.key === "ArrowRight" ? 1 : -1;
        selectTab((index + delta + tabs.length) % tabs.length);
    };

    React.useEffect(() => {
        const hash = getWindow()?.location.hash;
        if (!hash) {
            return;
        }
        const index = tabs.findIndex(
            (tab, i) => `#tab-${slugifyName(tab.name)}-${i + 1}` === hash,
        );
        if (index > 0) {
            setActiveIndex(index);
        }
    }, []);

    return (
        <section
            className={concatClassNames([
                "tabs-block",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <div role="tablist" aria-label={v.aria_label || "Tabs Block"}>
                {tabs.map((tab, i) => {
                    const counter = i + 1;
                    const slug = slugifyName(tab.name);
                    const active = i === activeIndex;
                    return (
                        <button
                            key={i}
                            ref={(el) => {
                                tabRefs.current[i] = el;
                            }}
                            id={`tab-${slug}-${counter}`}
                            type="button"
                            role="tab"
                            aria-controls={`panel-${slug}-${counter}`}
                            tabIndex={active ? 0 : -1}
                            aria-selected={active}
                            onClick={() => selectTab(i)}
                            onKeyDown={(event) => onKeyDown(event, i)}
                        >
                            {tab.name}
                        </button>
                    );
                })}
            </div>
            {tabs.map((tab, i) => {
                const counter = i + 1;
                const slug = slugifyName(tab.name);
                const active = i === activeIndex;
                return (
                    <div
                        key={i}
                        id={`panel-${slug}-${counter}`}
                        role="tabpanel"
                        aria-labelledby={`tab-${slug}-${counter}`}
                        hidden={active ? undefined : true}
                    >
                        {props.renderPanel(tab.panel ?? [])}
                    </div>
                );
            })}
        </section>
    );
};
